import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for HIS & RCM) ---
import {
    // Core Concepts & General
    faLifeRing,
    faLaptopMedical,    // HIS / Healthcare IT
    faNetworkWired,     // Integration / Network
    faDatabase,         // Data / Records Management
    faShieldAlt,        // Security / Compliance / HIPAA
    faTasks,            // Efficiency / Operations
    faHeartPulse,       // Patient Care / Outcomes
    faUsersCog,         // Resource Management / Staff Portal
    faDollarSign,       // Cost Savings / Finance / RCM
    faCheckCircle,      // Quality / Compliance / Completion
    faInfoCircle,       // Information Access
    // HIS Services
    faSitemap,          // IT Integration (Departments)
    faMagic,            // Digitization (of Manual Processes)
    faWrench,           // IT Installation / Maintenance / AMC
    faCogs,             // Capability Management / Configuration
    // faUsersCog used again for Staff Portal
    faChalkboardTeacher,// Training & Support
    faExchangeAlt,      // Data Porting / Parallel Run
    faPlayCircle,       // Go Live / Post-Go Live
    // faWrench / faSyncAlt already listed for AMC
    // RCM
    faFileInvoiceDollar,// RCM / Billing / Coding
    faUserCheck,        // Insurance Verification
    faClipboardCheck,   // Claim Management
    faCashRegister,     // Payment Posting / Follow-up
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
     faMicroscope, faTools, faLightbulb, faHandshake, faWeightHanging, faShoppingCart,
     faNetworkWired, faComments, faUserTie, faLifeRing, faGlobe, faLeaf, faExpandArrowsAlt,
     faBiohazard, faAward, faProjectDiagram, faPalette, faDirections, faRulerCombined,
     faBuilding, faChartLine, faSearchDollar, faSmileBeam, faFileInvoiceDollar, faReceipt,
     faStream, faBullhorn, faSyncAlt
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // HIS Importance/Benefits
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // HIS Services
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // RCM Section
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Conclusion

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for HIS/tech/data
    const heroBgImage = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Data/dashboard image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Hospital Information System (HIS) Solutions</h1>
            <p className="cliniqor-sub-heading">
              Streamlining Healthcare Operations with Cutting-Edge IT by Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              A well-integrated HIS is essential for managing complex operations, reducing manual processes, increasing efficiency, and ensuring accurate, timely patient care. Cliniqor provides tailored HIS solutions for your unique needs.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- IMPORTANCE OF HIS (Features Section - 6 items) --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>The Importance of a Strong Hospital Information System</h2>
              <p>Integrating and managing operations into a single unified system.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (6 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHeartPulse} /></div>
                    <h4>1. Improved Patient Care</h4>
                    <p>Accessible information enables faster decisions, accurate treatments, better outcomes.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                    <h4>2. Enhanced Efficiency</h4>
                    <p>Automating administrative tasks allows staff to focus on critical patient care duties.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <h4>3. Better Resource Management</h4>
                    <p>Centralized tracking of medications, equipment, and staff schedules ensures efficient use.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <h4>4. Cost Savings</h4>
                    <p>Automation and streamlined operations reduce overhead costs and resource waste.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                    <h4>5. Data Security & Compliance</h4>
                    <p>Ensuring secure patient data, regulatory compliance (HIPAA), and minimizing breach risks.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faInfoCircle} /></div>
                    <h4>6. Real-time Information Access</h4>
                    <p>Up-to-date patient information accessible anytime for informed, timely decisions.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- HIS SERVICES (Detailed Planning Section - 9 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Hospital Information System Services</h2>
               <p>Tailored solutions ensuring seamless operation across departments.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for HIS Services (9 items - will be long) */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div>
                    <div className="cliniqor-content"><h4>1. Comprehensive IT Integration</h4> <p>Unifying departments (inpatient, outpatient, lab, pharmacy, radiology, billing) for seamless data flow.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMagic} /></div>
                    <div className="cliniqor-content"><h4>2. Digitization of Manual Processes</h4> <p>Automating registration, scheduling, billing, discharge to reduce errors and accelerate workflow.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faWrench} /></div>
                    <div className="cliniqor-content"><h4>3. IT Installation, Support & Maintenance</h4> <p>End-to-end hardware/software integration, continuous support, and updates for peak performance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCogs} /></div>
                    <div className="cliniqor-content"><h4>4. Capability Management & Configuration</h4> <p>Customizing the HIS to handle complexities of care, finance, and clinical functions effectively.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsersCog} /></div> {/* Staff Portal */}
                    <div className="cliniqor-content"><h4>5. Employee and Staff Portal Development</h4> <p>Providing easy access to information, schedules, patient data to streamline communication.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>6. Training and Support</h4> <p>Comprehensive staff training to ensure proficient system utilization and ongoing assistance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faExchangeAlt} /></div>
                    <div className="cliniqor-content"><h4>7. Data Porting & Parallel Run Support</h4> <p>Migrating existing data and running systems simultaneously for smooth transition.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPlayCircle} /></div>
                    <div className="cliniqor-content"><h4>8. Go Live & Post-Go Live Support</h4> <p>Ensuring efficient transition and providing ongoing technical help, training, and upgrades.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLifeRing} /></div> {/* AMC Support */}
                    <div className="cliniqor-content"><h4>9. Ongoing Support & Upgrades (AMC)</h4> <p>Comprehensive support/upgrades under AMC ensuring system remains functional, secure, up-to-date.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- REVENUE CYCLE MANAGEMENT (RCM) (Reusing Features Layout - 4 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Revenue Cycle Management: Streamlining Financial Operations</h2>
                 <p>Integrated RCM solutions merging business and clinical sides for stability.</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ marginBottom: '30px' }}>
                  <p>RCM tracks patient care episodes from registration to final payment. Efficient RCM improves cash flow, reduces costs, and enhances financial transparency.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for RCM Services --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faFileInvoiceDollar} /></div>
                            <h4>Medical Billing & Coding</h4>
                            <p>Ensuring accurate coding and submission to reduce claim denials and delays.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserCheck} /></div>
                            <h4>Insurance Verification</h4>
                            <p>Streamlining coverage verification for valid claims and prompt reimbursement.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                            <h4>Claim Management</h4>
                            <p>Seamless tracking to reduce submission-to-payment time and minimize bottlenecks.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCashRegister} /></div>
                            <h4>Payment Posting & Follow-up</h4>
                            <p>Timely posting and tracking outstanding balances to ensure full payment collection.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* --- CONCLUSION SECTION (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Elevating Hospital Performance with Advanced IT Solutions</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>An essential component for enhancing efficiency, care, and sustainability. Cliniqor offers tailored HIS solutions, from integration and automation to ongoing support, equipping your facility with best-in-class IT infrastructure.</p>
                   <p>Leverage our HIS expertise for greater efficiency, reduced errors, and superior care. Our RCM services ensure streamlined financial processes, maximizing profitability. Let Cliniqor transform your hospital into a highly efficient, technologically advanced facility ready for the modern healthcare landscape.</p>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Upgrade Your Hospital's IT Infrastructure</h3>
          <p>Partner with Cliniqor for cutting-edge Hospital Information System and Revenue Cycle Management solutions. Contact us today to enhance efficiency and patient care.</p>
          <a href="#contact" className="cliniqor-cta-button">Request HIS/RCM Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;